import React, { useState } from 'react';
import { useCollection } from '@amzn/awsui-collection-hooks';
import {
    Box,
    Button,
    Header,
    Pagination,
    Table,
    TextFilter,
    SpaceBetween,
    CollectionPreferences,
    Link,
} from '@amzn/awsui-components-console';
import * as Constants from './constants';

// empty state of the table
function EmptyState({ title, subtitle, action }) {
    return (
        <Box textAlign="center" color="inherit">
            <Box variant="strong" textAlign="center" color="inherit">
                {title}
            </Box>
            <Box variant="p" padding={{ bottom: 's' }} color="inherit">
                {subtitle}
            </Box>
            {action}
        </Box>
    );
}

/**
 * get filtered item count
 * @param {*} filteredItemsCount
 * @returns
 */
function getFilterCounterText(filteredItemsCount) {
    if (filteredItemsCount <= 1) {
        return `${filteredItemsCount} match`;
    } else {
        return `${filteredItemsCount} matches`;
    }
}

/**
 * get table header
 * @param items: test runs
 * @param refresh: refresh button
 * @return {String} test run table header with the number of test runs and a refresh button
 */
function getHeader(items, refresh) {
    // TODO: we may add a csv download button if needed
    return (
        <Header
            counter={`(${items.length})`}
            actions={
                <SpaceBetween direction="horizontal" size="xs">
                    <Button onClick={() => refresh()} iconName="refresh" variant="icon" />
                </SpaceBetween>
            }
            description="To view detailed test run results, click the link under column Test result."
        >
            ADF test runs
        </Header>
    );
}

export default function CollectionHooksTable(allItems, columnDefinitions, visibleColumns, loading, refresh) {
    const [preferences, setPreferences] = useState({
        pageSize: 20,
        stickyColumns: { first: 1 },
        visibleContent: visibleColumns,
    });
    const { items, actions, collectionProps, filteredItemsCount, filterProps, paginationProps } = useCollection(
        allItems,
        {
            filtering: {
                noMatch: (
                    <EmptyState
                        title="No matches"
                        subtitle="We can’t find a match."
                        action={<Button onClick={() => actions.setFiltering('')}>Clear filter</Button>}
                    />
                ),
            },
            pagination: { pageSize: preferences.pageSize },
            sorting: {
                defaultState: { sortingColumn: Constants.SORTED_FILED, isDescending: true },
            },
            selection: {},
        }
    );
    return (
        <Table
            {...collectionProps}
            header={getHeader(allItems, refresh)}
            columnDefinitions={columnDefinitions}
            visibleColumns={preferences.visibleContent}
            items={items}
            pagination={<Pagination {...paginationProps} />}
            filter={<TextFilter {...filterProps} countText={getFilterCounterText(filteredItemsCount)} />}
            empty={
                <Box textAlign="center" color="inherit">
                    <Box padding={{ bottom: 's' }} variant="p" color="inherit">
                        No test runs to display. Cut us a{' '}
                        <Link
                            external
                            href="https://t.corp.amazon.com/create/templates/7b41de12-761a-40e2-98b6-c302121b351a"
                        >
                            {' '}
                            ticket{' '}
                        </Link>{' '}
                        if you believe there is an error.
                    </Box>
                </Box>
            }
            loading={loading}
            loadingText="Loading test runs"
            stickyColumns={preferences.stickyColumns}
            resizableColumns
            preferences={
                <CollectionPreferences
                    {...Constants.COLLECTION_PREFERENCES_PROPS}
                    preferences={preferences}
                    onConfirm={({ detail }) => setPreferences(detail)}
                />
            }
        />
    );
}
